import React, { createContext, useContext, useState, useRef, useEffect, useCallback, startTransition, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { buildAmbientAudioUrl, normalizeManagedAudioUrl } from '@/lib/audioAssetUrls';

interface AudioTrack {
  id: string;
  name: string;
  file_url: string;
  file_type: string | null;
}

interface AudioState {
  currentTrack: AudioTrack | null;
  isPlaying: boolean;
  volume: number;
  tracks: AudioTrack[];
  isLoading: boolean;
  error: string | null;
}

interface AudioContextType extends AudioState {
  playTrack: (track: AudioTrack) => Promise<void>;
  pauseAudio: () => void;
  stopAudio: () => void;
  setVolume: (volume: number) => void;
  refreshTracks: () => Promise<void>;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

// Fallback tracks
const FALLBACK_TRACKS: AudioTrack[] = [
  {
    id: 'rain',
    name: 'Gentle Rain',
    file_url: buildAmbientAudioUrl('rain.mp3'),
    file_type: 'audio/mpeg'
  },
  {
    id: 'ocean',
    name: 'Ocean Waves',
    file_url: buildAmbientAudioUrl('ocean.mp3'),
    file_type: 'audio/mpeg'
  },
  {
    id: 'forest',
    name: 'Forest Ambience',
    file_url: buildAmbientAudioUrl('forest.mp3'),
    file_type: 'audio/mpeg'
  },
  {
    id: 'cafe',
    name: 'Coffee Shop',
    file_url: buildAmbientAudioUrl('cafe.mp3'),
    file_type: 'audio/mpeg'
  }
];

interface AudioProviderProps {
  children: ReactNode;
}

const MEDIA_ERR_ABORTED = 1; // Aligns with HTMLMediaElement MediaError.MEDIA_ERR_ABORTED

export const SimplifiedAudioProvider: React.FC<AudioProviderProps> = ({ children }) => {
  const [state, setState] = useState<AudioState>({
    currentTrack: null,
    isPlaying: false,
    volume: 0.4,
    tracks: [],
    isLoading: false,
    error: null,
  });

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const volumeRef = useRef(state.volume);

  // Keep volumeRef in sync with state
  useEffect(() => {
    volumeRef.current = state.volume;
  }, [state.volume]);

  const resetAudioElement = (audio: HTMLAudioElement | null) => {
    if (!audio) return;

    audio.pause();
    audio.oncanplay = null;
    audio.onerror = null;
    audio.src = '';
    audio.load();
  };

  // Fetch tracks from database
  const fetchTracks = async (): Promise<AudioTrack[]> => {
    try {
      const { data, error } = await supabase
        .from('audio_tracks')
        .select('id, name, file_url, file_type')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      return data?.length > 0
        ? data.map((track) => ({
            ...track,
            file_url: normalizeManagedAudioUrl(track.file_url),
          }))
        : FALLBACK_TRACKS;
    } catch (error) {
      console.error('Error fetching tracks:', error);
      return FALLBACK_TRACKS;
    }
  };

  // Load tracks on mount
  useEffect(() => {
    const loadTracks = async () => {
      // Transitions: this provider mounts above the router's Suspense
      // boundary, and these updates land while SSR'd routes are still
      // hydrating. Non-transition updates there force React to throw away
      // the server HTML and client-render (minified error #421).
      startTransition(() => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
      });

      try {
        const tracks = await fetchTracks();
        startTransition(() => {
          setState(prev => ({
            ...prev,
            tracks,
            isLoading: false,
            error: null
          }));
        });
      } catch (error) {
        startTransition(() => {
          setState(prev => ({
            ...prev,
            tracks: FALLBACK_TRACKS,
            isLoading: false,
            error: 'Using fallback sounds'
          }));
        });
      }
    };

    loadTracks();
  }, []);

  // Set up real-time subscription for track changes
  useEffect(() => {
    const channel = supabase
      .channel('audio_tracks_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'audio_tracks'
        },
        () => {
          // Refresh tracks when admin makes changes
          refreshTracks();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Refresh tracks
  const refreshTracks = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      const tracks = await fetchTracks();
      setState(prev => ({ 
        ...prev, 
        tracks, 
        isLoading: false,
        error: null 
      }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        error: 'Failed to refresh tracks'
      }));
    }
  }, []);

  // Play track
  const playTrack = useCallback(async (track: AudioTrack) => {
    try {
      // Stop and clean up any current audio
      if (audioRef.current) {
        resetAudioElement(audioRef.current);
        audioRef.current = null;
      }

      const audio = new Audio(track.file_url);
      audio.loop = true;
      audio.volume = volumeRef.current;
      audioRef.current = audio;

      audio.oncanplay = () => {
        setState(prev => ({ ...prev, isLoading: false }));
      };

      audio.onerror = (event) => {
        const mediaError = audio.error;
        const isAbortError = mediaError?.code === MEDIA_ERR_ABORTED;

        if (isAbortError) {
          // Ignore abort errors triggered when the user changes tracks quickly
          return;
        }

        console.error('Audio error:', event);
        setState(prev => ({
          ...prev,
          isLoading: false,
          isPlaying: false,
          error: 'Failed to play audio'
        }));
      };

      setState(prev => ({
        ...prev,
        currentTrack: track,
        isLoading: true,
        error: null
      }));

      const playPromise = audio.play();

      if (playPromise !== undefined) {
        await playPromise;
      }

      setState(prev => ({
        ...prev,
        isPlaying: true,
        isLoading: false,
        error: null,
      }));

    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        // A new play request interrupted this one – it's safe to ignore
        return;
      }

      console.error('Error playing track:', error);
      setState(prev => ({
        ...prev,
        isPlaying: false,
        isLoading: false,
        error: 'Failed to play audio'
      }));
    }
  }, []);

  // Pause audio
  const pauseAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setState(prev => ({ ...prev, isPlaying: false }));
    }
  }, []);

  // Stop audio
  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      resetAudioElement(audioRef.current);
      audioRef.current = null;
    }
    setState(prev => ({
      ...prev,
      isPlaying: false,
      currentTrack: null,
      isLoading: false,
      error: null,
    }));
  }, []);

  // Set volume
  const setVolume = useCallback((volume: number) => {
    setState(prev => ({ ...prev, volume }));
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        resetAudioElement(audioRef.current);
        audioRef.current = null;
      }
    };
  }, []);

  const contextValue: AudioContextType = {
    ...state,
    playTrack,
    pauseAudio,
    stopAudio,
    setVolume,
    refreshTracks,
  };

  return (
    <AudioContext.Provider value={contextValue}>
      {children}
    </AudioContext.Provider>
  );
};

export const useSimplifiedAudio = (): AudioContextType => {
  const context = useContext(AudioContext);
  if (context === undefined) {
    throw new Error('useSimplifiedAudio must be used within a SimplifiedAudioProvider');
  }
  return context;
};
